import React from 'react';
import KoobPagination from '@koob/ui/src/components/KoobPagination';
import { sortByCompatibility } from '@koob/utils';
import TripHotelCard from './TripHotelCard';
import { useSearchHotels } from '../hooks/useTripStoreSelectors';
import KoobPlaceholder from '@koob/ui/src/components/KoobPlaceholder';

interface TripListProps {
  experiences: any[];
  hotels: any[];
  isLoading?: boolean;
  areProgramsLoading?: boolean;
  addExperience: (experienceId: string) => void;
  addHotel: (hotel: any) => void;
  getExperienceCompatibility: (experience: any) => string | null;
  page: number;
  setPage: (page: number) => void;
  totalPages: number;
  totalCount: number;
  perPage: number;
  setPerPage: (perPage: number) => void;
  // Already resolved by the caller — which text to show (a count, an empty
  // state, or nothing while loading) is a translation/business-copy choice
  // that belongs in the app, not in this package.
  resultsText?: React.ReactNode;
  renderExperienceCard: (
    experience: any,
    options: { compatibility: string | null; onSelect: () => void },
  ) => React.ReactNode;
  locale?: string;
  isForTrip?: boolean;
  // The label/render-prop bundle from each app's own `useTripHotelCardProps`
  // hook — passed through to `TripHotelCard` (renderPrice, labels, etc.),
  // typed loosely so the spread below doesn't need this package to know
  // that hook's app-local shape.
  tripHotelCardProps?: any;
}

// KD-3391: rendered inside the compose right panel (ComposeMap list tab),
// not as a standalone page — the panel owns the title and view tabs.
export default function TripList({
  experiences,
  hotels,
  isLoading,
  areProgramsLoading,
  addExperience,
  addHotel,
  getExperienceCompatibility,
  page,
  setPage,
  totalPages,
  totalCount,
  perPage,
  setPerPage,
  resultsText,
  renderExperienceCard,
  locale,
  isForTrip,
  tripHotelCardProps,
}: TripListProps) {
  const searchHotels = useSearchHotels();

  return (
    <div className="p-5">
      <div className="text-sm text-gray-500">{resultsText}</div>

      <div className="mt-3 grid gap-5 md:grid-cols-2 2xl:grid-cols-3">
        {(isLoading || areProgramsLoading) && (
          <KoobPlaceholder count={6} className="h-80 rounded-lg" />
        )}
        {!searchHotels &&
          sortByCompatibility(experiences, getExperienceCompatibility).map(
            (experience) => (
              <React.Fragment key={experience?.id}>
                {renderExperienceCard(experience, {
                  compatibility: getExperienceCompatibility(experience),
                  onSelect: () => addExperience(experience?.id),
                })}
              </React.Fragment>
            ),
          )}

        {searchHotels &&
          hotels?.map((hotel) => (
            <TripHotelCard
              key={hotel.id}
              hotel={hotel}
              addHotel={addHotel}
              hotelWithPrice={hotel}
              isForTrip={isForTrip}
              locale={locale}
              {...tripHotelCardProps}
            />
          ))}
      </div>

      {searchHotels && hotels?.length > 0 && (
        <div className="mt-4">
          <KoobPagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            totalCount={totalCount}
            perPage={perPage}
            setPerPage={setPerPage}
          />
        </div>
      )}
    </div>
  );
}
